import React, { useState } from 'react';
import {
  Container,
  Title,
  Group,
  Paper,
  Stack,
  Button,
  Text,
  Progress,
  Alert,
  FileButton,
  Card,
  Badge,
  Divider,
} from '@mantine/core';
import {
  IconFileSpreadsheet,
  IconUpload,
  IconCheck,
  IconAlertCircle,
  IconTrash,
} from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { storicoService } from '../../lib/storico-service';
import { notifications } from '@mantine/notifications';

export function StoricoAdminPage() {
  const [file, setFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const queryClient = useQueryClient();

  // Query per ottenere il conteggio
  const { data: count, isLoading: countLoading } = useQuery({
    queryKey: ['storico-count'],
    queryFn: () => storicoService.getCount(),
  });

  // Mutation per l'import
  const importMutation = useMutation({
    mutationFn: (file: File) => storicoService.importFromExcel(file),
    onSuccess: (importedCount) => {
      notifications.show({
        title: 'Import completato',
        message: `${importedCount} record importati con successo`,
        color: 'green',
        icon: <IconCheck size={16} />,
      });
      setFile(null);
      setUploadProgress(0);
      queryClient.invalidateQueries({ queryKey: ['storico'] });
      queryClient.invalidateQueries({ queryKey: ['storico-count'] });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Errore import',
        message: error?.response?.data?.message || 'Errore durante l\'import del file',
        color: 'red',
        icon: <IconAlertCircle size={16} />,
      });
      setUploadProgress(0);
    },
  });

  // Mutation per cancellare tutti i dati
  const clearMutation = useMutation({
    mutationFn: () => storicoService.clearAll(),
    onSuccess: () => {
      notifications.show({
        title: 'Dati cancellati',
        message: 'Tutti i record dello storico sono stati eliminati',
        color: 'green',
        icon: <IconCheck size={16} />,
      });
      queryClient.invalidateQueries({ queryKey: ['storico'] });
      queryClient.invalidateQueries({ queryKey: ['storico-count'] });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Errore',
        message: error?.response?.data?.message || 'Errore durante la cancellazione',
        color: 'red',
        icon: <IconAlertCircle size={16} />,
      });
    },
  });

  const handleFileSelect = (selectedFile: File | null) => {
    if (selectedFile) {
      // Verifica che sia un file Excel
      if (
        !selectedFile.name.endsWith('.xlsx') &&
        !selectedFile.name.endsWith('.xls')
      ) {
        notifications.show({
          title: 'Formato non valido',
          message: 'Il file deve essere in formato Excel (.xlsx o .xls)',
          color: 'red',
          icon: <IconAlertCircle size={16} />,
        });
        return;
      }
      setFile(selectedFile);
    }
  };

  const handleImport = () => {
    if (!file) return;

    setUploadProgress(30);
    importMutation.mutate(file);
  };

  const handleClearAll = () => {
    if (
      window.confirm(
        'Sei sicuro di voler cancellare TUTTI i dati dello storico? Questa operazione non può essere annullata.'
      )
    ) {
      clearMutation.mutate();
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <Container size="lg" py="xl">
      <Stack gap="md">
        {/* Header */}
        <Group justify="space-between">
          <Group>
            <IconFileSpreadsheet size={32} />
            <Title order={2}>Gestione Storico - Admin</Title>
          </Group>
        </Group>

        {/* Info Card */}
        <Card withBorder p="lg">
          <Group justify="space-between" mb="md">
            <Text size="lg" fw={500}>
              Database Storico
            </Text>
            <Badge size="lg" variant="filled" color={count && count > 0 ? 'blue' : 'gray'} style={{ color: 'white' }}>
              {countLoading ? 'Caricamento...' : `${count || 0} record`}
            </Badge>
          </Group>
          <Text size="sm" c="dimmed">
            Gestisci i dati storici dei progetti. Puoi importare dati da file Excel o
            cancellare tutti i record esistenti.
          </Text>
        </Card>

        <Divider />

        {/* Import Section */}
        <Paper shadow="sm" p="lg" withBorder>
          <Stack gap="md">
            <Group justify="space-between">
              <Text size="lg" fw={500}>
                Import da Excel
              </Text>
              <Badge color="blue">Progetti.xlsx</Badge>
            </Group>

            <Alert
              icon={<IconAlertCircle size={16} />}
              title="Formato File"
              color="blue"
              variant="light"
            >
              <Text size="sm">
                Il file Excel deve contenere le seguenti colonne:
              </Text>
              <Text size="xs" c="dimmed" mt="xs">
                Categoria, Num Decoro, Nome Decoro, Forma Bianco, Ordine Produzione,
                Elaborazione, Stato Progetto, Fine Sviluppo Impianti/Pellicole, Data
                Completamento, Tipologia Statistica, CodDecoro, Decoro Produzione, Ot,
                Riferimento Prog N, Item Type, Path
              </Text>
            </Alert>

            <Stack gap="sm">
              <FileButton onChange={handleFileSelect} accept=".xlsx,.xls">
                {(props) => (
                  <Button
                    {...props}
                    leftSection={<IconFileSpreadsheet size={16} />}
                    variant="light"
                    fullWidth
                    disabled={importMutation.isPending}
                  >
                    Seleziona File Excel
                  </Button>
                )}
              </FileButton>

              {file && (
                <Card withBorder p="md" bg="gray.0">
                  <Group justify="space-between">
                    <Group gap="xs">
                      <IconFileSpreadsheet size={20} color="green" />
                      <div>
                        <Text size="sm" fw={500}>
                          {file.name}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {formatFileSize(file.size)}
                        </Text>
                      </div>
                    </Group>
                    <Button
                      size="xs"
                      variant="subtle"
                      color="red"
                      onClick={() => setFile(null)}
                      disabled={importMutation.isPending}
                    >
                      Rimuovi
                    </Button>
                  </Group>
                </Card>
              )}

              {importMutation.isPending && (
                <Progress value={uploadProgress} animated color="blue" />
              )}

              <Button
                leftSection={<IconUpload size={16} />}
                onClick={handleImport}
                disabled={!file || importMutation.isPending}
                loading={importMutation.isPending}
                fullWidth
                size="lg"
              >
                {importMutation.isPending ? 'Import in corso...' : 'Importa Dati'}
              </Button>
            </Stack>
          </Stack>
        </Paper>

        <Divider />

        {/* Danger Zone */}
        <Paper shadow="sm" p="lg" withBorder>
          <Stack gap="md">
            <Group justify="space-between">
              <div>
                <Text size="lg" fw={500} c="red">
                  Zona Pericolosa
                </Text>
                <Text size="sm" c="dimmed" mt={4}>
                  Operazioni irreversibili sui dati
                </Text>
              </div>
            </Group>

            <Alert
              icon={<IconAlertCircle size={16} />}
              title="Attenzione"
              color="red"
              variant="light"
            >
              Cancellando tutti i dati, perderai definitivamente tutti i record dello
              storico. Questa operazione non può essere annullata.
            </Alert>

            <Button
              leftSection={<IconTrash size={16} />}
              color="red"
              variant="light"
              onClick={handleClearAll}
              disabled={clearMutation.isPending || (count || 0) === 0}
              loading={clearMutation.isPending}
              fullWidth
            >
              {clearMutation.isPending
                ? 'Cancellazione in corso...'
                : 'Cancella Tutti i Dati'}
            </Button>
          </Stack>
        </Paper>
      </Stack>
    </Container>
  );
}
